"use client";

import Image from "next/image";
import { Link } from "@/navigation";
import { useTranslations } from "next-intl";

const IMAGE_BASE_PATH = "/image/new-version/machining/five-axis/bmcl";

const SURFACE_FINISHES = [
	{ key: "naturalGloss", image: "natural-gloss.webp" },
	{ key: "matte", image: "matte.webp" },
	{ key: "electropolish", image: "electropolish.webp" },
	{ key: "electrophoresis", image: "electrophoreses.webp" },
	{ key: "chromeplate", image: "chromeplate.webp" },
	{ key: "goldPlating", image: "gold-plating.webp" },
	{ key: "blackOxide", image: "black-oxide.webp" },
	{ key: "blackGlossy", image: "black-glossy.webp" },
	{ key: "polishRedCopper", image: "polish-red-copper.webp" },
	{ key: "goldChromate", image: "gold-chromate.webp" },
	{ key: "carbonization", image: "carbonization.webp" },
	{ key: "hardenOxide", image: "harden-oxide.webp" },
];

export interface AxisBmclProps {
	/** 自定义主标题，不传则使用多语言默认值 */
	title?: string;
	/** 自定义描述，不传则使用多语言默认值 */
	subtitle?: string;
	/** 标题布局模式: "split" 左右分栏(默认) | "center" 居中 */
	titleLayout?: "split" | "center";
}

const AxisBmcl = ({ title, subtitle, titleLayout = "split" }: AxisBmclProps) => {
	const t = useTranslations("fiveAxisSurfaceFinishes");

	return (
		<section className="w-full bg-[#ededed] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 标题区域 */}
				{titleLayout === "center" ? (
				<div className="mx-auto mb-6 sm:mb-8 md:mb-10 lg:mb-12 max-w-4xl text-center">
					<h2 className="mb-3 sm:mb-4 text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{title ?? t("title")}
					</h2>
					<p className="text-sm leading-relaxed md:text-base text-black">
							{subtitle ?? t("subtitle")}
						</p>
					</div>
				) : (
				<div className="mb-6 sm:mb-8 md:mb-10 lg:mb-12 grid gap-6 lg:grid-cols-[1fr_1.2fr] lg:items-center lg:gap-12 2xl:gap-x-32">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{title ?? t("title")}
					</h2>
					<p className="text-sm leading-relaxed md:text-base text-black">
							{subtitle ?? t("subtitle")}
						</p>
					</div>
				)}

				{/* 表面处理卡片网格 */}
				<div className="grid grid-cols-2 gap-0 md:grid-cols-3 lg:grid-cols-4">
					{SURFACE_FINISHES.map((finish) => (
						<div
							key={finish.key}
							className="flex flex-col border border-[#bfbfbf] p-4 md:p-10"
						>
							{/* 标题 */}
							<h3 className="mb-3 text-sm font-bold text-black md:text-base">
								{t(`${finish.key}Title` as any)}
							</h3>
							{/* 图片 */}
							<div className="relative mb-4 aspect-square w-full overflow-hidden">
								<Image
									src={`${IMAGE_BASE_PATH}/${finish.image}`}
									alt={t(`${finish.key}Title` as any)}
									fill
									className="object-contain"
									sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
								/>
							</div>
							{/* 描述 */}
							<p className="!leading-relaxed text-black text-sm">
								{t(`${finish.key}Desc` as any)}
							</p>
						</div>
					))}
				</div>

				{/* 底部按钮 */}
				<div className="mt-12 flex justify-center md:mt-16">
					<Link
						href="/surface-finishing"
						className="inline-flex items-center justify-center rounded bg-themePrimary400 hover:text-white px-6 py-3 text-sm font-medium text-white transition-all duration-300 hover:bg-[#5a9420] md:px-8 md:py-3.5 md:text-base"
					>
						{t("moreButton")}
					</Link>
				</div>
			</div>
		</section>
	);
};

export default AxisBmcl;
